<script setup lang="ts">
import { useBreakpoints } from '@vueuse/core'
import { ChartDataItem } from '~/types/chart'
import { useColors } from '~/composables/colors'

const { formattedNumber } = useFormattedNumber()
interface ChartProps {
  data: ChartDataItem[]
  direction?: 'horizontal' | 'vertical'
}
const props = defineProps<ChartProps>()
const { mapKeysToColors } = useColors()
const colors = computed(() => mapKeysToColors(props.data.map((item) => item.label)))

const maxValue = computed(() => Math.max(...props.data.map((item) => item.value)))
const breakpoints = useBreakpoints({
  tablet: 769,
})
const tabletUp = breakpoints.greater('tablet')
const compiledDirection = computed(() => {
  if (props.direction === 'vertical' && tabletUp) {
    return 'vertical'
  } else {
    return 'horizontal'
  }
})
</script>
<template>
  <div class="stack" :class="compiledDirection">
    <template v-for="(dataItem, i) in data" :key="i">
      <p class="stack__item--content">{{ dataItem.label }}</p>
      <div class="stack__item--bar">
        <div
          :style="[
            compiledDirection === 'vertical'
              ? `height: ${(dataItem.value / maxValue) * 100}%; width: 100%`
              : `width: ${(dataItem.value / maxValue) * 100}%; height: 100%`,
            { backgroundColor: colors[dataItem.label] },
          ]
          "
        ></div>
        <span class="value">{{ formattedNumber(dataItem.value) }}</span>
      </div>
    </template>
  </div>
</template>
<style lang="scss" scoped>
@import 'App.scss';

.stack {
  display: grid;
  width: 100%;
  gap: 10px;

  &__item {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    flex: 1 1 0;

    &--bar {
      display: flex;
      align-items: center;
      gap: 0.5rem;

      div {
        transition: all 1s ease-in-out;
        background-color: $darkGreen;
        min-height: 2px;
        border-top-left-radius: 6px;
        border-top-right-radius: 6px;
      }

      .value {
        font-size: 12px;
        font-weight: 400;
        white-space: nowrap;
      }
    }

    &--content {
      font-size: 12px;
      font-weight: 400;
      text-transform: capitalize;
      margin: 0;
    }
  }

  &.vertical {
    height: 25vh;
    grid-auto-flow: column;
    grid-template-rows: 6fr 1fr;
    grid-auto-flow: dense;
    grid-template-columns: repeat(auto-fit, minmax(0, 1fr));

    .stack {
      &__item {
        width: 20px;

        &--bar {
          display: flex;
          flex-direction: column-reverse;
        }

        &--content {
          grid-row: 2;
          text-align: center;
        }
      }
    }
  }

  &.horizontal {
    grid-template-columns: auto 1fr;

    .stack {
      &__item {
        height: 20px;
        display: grid;
        grid-template-columns: 140px 1fr;

        &--bar {
          div {
            background-color: $darkGreen;
            transition: all 0.7s ease-in-out;
            border-top-left-radius: 0;
            border-top-right-radius: 0;
            border-bottom-right-radius: 6px;
            border-top-right-radius: 6px;
          }
        }
      }
    }
  }
}
</style>
